import React, { useEffect, useState } from 'react';
import { ArrowRight, CaretDown, CaretRight, CaretUp, CircleNotch, Clock, EnvelopeSimple, PenNib, Tray } from '@phosphor-icons/react';
import { supabase } from '../../lib/supabase/client';
import { useIslandActions } from '../island/IslandProvider';
import EmailVerfassen from '../../pages/admin/emails/EmailVerfassen';
import { replyKontextAusEmail } from '../../lib/emails/verfassenKontext';
import type { BriefingMail, BriefingPrio } from '../../lib/dashboard/briefing';
import { istMeineZustaendigkeit, type DashboardScope, type ScopeNutzer } from '../../lib/dashboard/scope';
import { DashboardWidget, KopfInfo, WidgetKopf, WidgetLeer } from './DashboardWidget';
import { MAIL_ANTWORT_META } from '../island/form/mailMeta';

interface Props {
  mails: BriefingMail[];
  onOpenEmail: (id: string) => void;
  scope: DashboardScope;
  nutzer: ScopeNutzer;
}

/** Je Gruppe zunächst so viele Mails zeigen, Rest über „+n weitere anzeigen“. */
const PRO_GRUPPE = 3;

const GRUPPEN: { prio: BriefingPrio; label: string; dot: string; tip: string; standardOffen: boolean }[] = [
  { prio: 'dringend', label: 'Sofort', dot: 'var(--bad)', tip: 'Sofort – Priorität dringend', standardOffen: true },
  { prio: 'hoch', label: 'Heute', dot: 'var(--warn)', tip: 'Heute – Priorität hoch', standardOffen: true },
  { prio: 'mittel', label: 'Diese Woche', dot: 'var(--dw-gelb)', tip: 'Diese Woche – Priorität mittel', standardOffen: false },
  { prio: 'niedrig', label: 'Kann warten', dot: 'var(--info)', tip: 'Kann warten – Priorität niedrig', standardOffen: false },
];

const chip = { height: 22, padding: '0 8px', borderRadius: 11, fontSize: 11, fontWeight: 800 } as const;
const knopf = { height: 34, padding: '0 13px', borderRadius: 17, gap: 6, fontSize: 12.5, fontWeight: 800, border: 'none', fontFamily: 'inherit' } as const;

const heuteIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const EmailAssistentWidget: React.FC<Props> = ({ mails, onOpenEmail, scope, nutzer }) => {
  const { openForm, closeForm } = useIslandActions();
  const [offen, setOffen] = useState<Record<string, boolean>>(
    Object.fromEntries(GRUPPEN.map((g) => [g.prio, g.standardOffen]))
  );
  const [konto, setKonto] = useState<{ id: string; email_adresse: string; bezeichnung: string } | null>(null);
  const [entwurfLaedt, setEntwurfLaedt] = useState<string | null>(null);
  // Kompakt: Mails einzeilig, Details nur für die aufgeklappte Mail; lange Gruppen gekürzt
  const [aufgeklappt, setAufgeklappt] = useState<string | null>(null);
  const [alleZeigen, setAlleZeigen] = useState<Record<string, boolean>>({});
  const heute = heuteIso();
  const vor24h = Date.now() - 24 * 60 * 60 * 1000;
  const sichtbareMails = scope === 'alle' ? mails
    : mails.filter((m) => istMeineZustaendigkeit(m.eff_zustaendig_id, m.eff_zustaendig_typ, nutzer));

  useEffect(() => {
    supabase.from('email_konten').select('id, email_adresse, bezeichnung, absender_name')
      .eq('aktiv', true).order('created_at', { ascending: true }).limit(1).maybeSingle()
      .then(({ data }) => {
        if (data) setKonto({ id: data.id, email_adresse: data.email_adresse, bezeichnung: data.bezeichnung || data.absender_name || data.email_adresse });
      });
  }, []);

  const antwortEntwerfen = async (m: BriefingMail) => {
    if (!konto || entwurfLaedt) return;
    setEntwurfLaedt(m.id);
    let autoText: string | undefined;
    try {
      const { data, error } = await supabase.functions.invoke('email-antwort-entwurf', { body: { email_id: m.id } });
      if (error) throw error;
      autoText = data?.entwurf || undefined;
    } catch {
      alert('KI-Entwurf fehlgeschlagen — Antwort öffnet ohne Vorschlag.');
    } finally {
      setEntwurfLaedt(null);
    }
    openForm(
      <EmailVerfassen
        variant="pille"
        konto={konto}
        replyTo={replyKontextAusEmail({ id: m.id, von: m.von, betreff: m.betreff, text_inhalt: m.text_inhalt || undefined, message_id: m.message_id || undefined, an: m.an, datum: m.datum } as any)}
        autoText={autoText}
        onClose={closeForm}
        onSent={closeForm}
      />,
      'Antwort entwerfen', 900, MAIL_ANTWORT_META,
    );
  };

  return (
    <DashboardWidget id="email-assistent" farbe="--dw-mail" icon={EnvelopeSimple} ariaLabel="E-Mail-Assistent">
      <WidgetKopf farbe="--dw-mail" icon={EnvelopeSimple} titel="E-Mail-Assistent" untertitel={`${sichtbareMails.length} offene Mails`}
        rechts={<KopfInfo tip="E-Mail-Assistent – Offene Mails mit KI-Analyse, nach Priorität gruppiert. Antworten lassen sich mit KI-Entwurf vorbereiten." />} />

      {sichtbareMails.length === 0 ? (
        <WidgetLeer icon={Tray} text={scope === 'meine' ? 'Keine offenen Mails in deinem Bereich' : 'Posteingang leer — nichts offen'} />
      ) : (
        GRUPPEN.map((g) => {
          const gruppe = sichtbareMails.filter((m) => m.prioritaet === g.prio);
          if (gruppe.length === 0) return null;
          const istOffen = offen[g.prio];
          return (
            <div key={g.prio} className="flex flex-col" style={{ gap: 8 }}>
              <button type="button" data-tip={g.tip} aria-expanded={istOffen}
                onClick={() => setOffen((o) => ({ ...o, [g.prio]: !o[g.prio] }))}
                className="dw-gruppe flex items-center self-start border-0"
                style={{ height: 32, padding: '0 12px 0 10px', borderRadius: 16, gap: 8, fontSize: 13, fontWeight: 800, color: 'var(--ink)', fontFamily: 'inherit', cursor: 'pointer' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: g.dot }} />
                {g.label}
                <span style={{ color: 'var(--ink3)', fontWeight: 700 }}>{gruppe.length}</span>
                {istOffen ? <CaretUp weight="bold" size={12} style={{ color: 'var(--ink3)' }} /> : <CaretDown weight="bold" size={12} style={{ color: 'var(--ink3)' }} />}
              </button>
              {istOffen && (
                <div className="flex flex-col" style={{ gap: 4 }}>
                  {(alleZeigen[g.prio] ? gruppe : gruppe.slice(0, PRO_GRUPPE)).map((m) => {
                    const fristFaellig = !!m.frist_datum && m.frist_datum <= heute;
                    const auf = aufgeklappt === m.id;
                    return (
                      <div key={m.id} className="flex flex-col"
                        style={{ borderRadius: 16, background: auf ? 'var(--fill)' : 'transparent', boxShadow: auf ? 'inset 0 0 0 1px var(--line)' : 'none' }}>
                        <button type="button" aria-expanded={auf} onClick={() => setAufgeklappt(auf ? null : m.id)}
                          className="dw-zeile flex w-full min-w-0 flex-wrap items-center border-0 bg-transparent text-left"
                          style={{ gap: '4px 8px', minHeight: 40, padding: '6px 10px', borderRadius: 16, fontFamily: 'inherit', cursor: 'pointer' }}>
                          {auf ? <CaretUp weight="bold" size={12} className="shrink-0" style={{ color: 'var(--ink3)' }} />
                            : <CaretRight weight="bold" size={12} className="shrink-0" style={{ color: 'var(--ink3)' }} />}
                          <span className="min-w-0 truncate" style={{ flex: '1 1 200px', fontSize: 13.5 }}>
                            <strong style={{ fontWeight: 800 }}>{m.absender_name || m.von}</strong>
                            <span style={{ color: 'var(--ink2)' }}> — {m.betreff}</span>
                          </span>
                          {Date.parse(m.datum) > vor24h && (
                            <span data-tip="Neu – In den letzten 24 Stunden eingegangen" className="flex shrink-0 items-center"
                              style={{ ...chip, background: 'var(--info-soft)', color: 'var(--info)' }}>Neu</span>
                          )}
                          {m.frist_datum && (
                            <span data-tip="Frist – Von der KI erkannte Frist" className="flex shrink-0 items-center whitespace-nowrap"
                              style={{ ...chip, background: fristFaellig ? 'var(--bad-soft)' : 'var(--fill2)', color: fristFaellig ? 'var(--bad)' : 'var(--ink2)' }}>
                              Frist {m.frist_datum.split('-').reverse().slice(0, 2).join('.')}.
                            </span>
                          )}
                          <span data-tip="Zeitschätzung – Geschätzter Aufwand laut KI" className="flex shrink-0 items-center whitespace-nowrap tabular-nums"
                            style={{ gap: 4, fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>
                            <Clock weight="bold" size={12} /> {m.zeitschaetzung_min ? `~${m.zeitschaetzung_min} Min.` : '—'}
                          </span>
                        </button>
                        {auf && (
                          <div className="flex flex-col" style={{ gap: 6, padding: '0 13px 11px 30px' }}>
                            {m.zusammenfassung && (
                              <span style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--ink2)' }}>{m.zusammenfassung}</span>
                            )}
                            {m.naechster_schritt && (
                              <span className="flex items-start" style={{ gap: 6, fontSize: 13, fontWeight: 700, color: 'var(--acc)' }}>
                                <ArrowRight weight="bold" size={13} className="shrink-0" style={{ marginTop: 2 }} />{m.naechster_schritt}
                              </span>
                            )}
                            <div className="flex flex-wrap" style={{ gap: 6, paddingTop: 2 }}>
                              <button type="button" onClick={() => antwortEntwerfen(m)} disabled={!konto || entwurfLaedt !== null}
                                data-tip="Antwort entwerfen – KI schreibt einen Entwurf, die Antwort öffnet in der Pille"
                                className="flex items-center disabled:opacity-50"
                                style={{ ...knopf, background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)', cursor: !konto || entwurfLaedt ? 'default' : 'pointer' }}>
                                {entwurfLaedt === m.id ? <CircleNotch weight="bold" size={14} className="animate-spin" /> : <PenNib weight="bold" size={14} />} Antwort entwerfen
                              </button>
                              <button type="button" onClick={() => onOpenEmail(m.id)} data-tip="Öffnen – E-Mail im E-Mail-Bereich anzeigen"
                                className="dw-knopf2 flex items-center" style={{ ...knopf, color: 'var(--ink)', cursor: 'pointer' }}>
                                <EnvelopeSimple weight="bold" size={14} /> Öffnen
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {gruppe.length > PRO_GRUPPE && (
                    <button type="button" onClick={() => setAlleZeigen((a) => ({ ...a, [g.prio]: !a[g.prio] }))}
                      className="self-start border-0 bg-transparent"
                      style={{ padding: '4px 10px', fontSize: 12.5, fontWeight: 700, color: 'var(--acc)', fontFamily: 'inherit', cursor: 'pointer' }}>
                      {alleZeigen[g.prio] ? 'Weniger anzeigen' : `+${gruppe.length - PRO_GRUPPE} weitere anzeigen`}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </DashboardWidget>
  );
};

export default EmailAssistentWidget;
